import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { CheckBox, CheckStyles, NewModal, MainContentPaddingTypes, NewSelect, NewInput, SystemControlsInner } from "../..";
import { NewFieldStyles, MenuFloats,  } from "../../../Definitions";
import { useSectionOptions } from "../../_Components";
import { useSection } from "../../../Services";
import { useLfContext } from "./_Modals/lfPenContext";

interface EmptySection_Props{
  isModalOpen: boolean;
  onClose: () => void;
  focus_view?: boolean;
}

const DEF_TRIGGER_AMOUNT = 30
const DEF_PAUSE_TIME = 2
  
export default function EmptySectionModal({isModalOpen, onClose, focus_view}:EmptySection_Props) {
  const { t } = useTranslation();
  const { state: { systemId, selectedSection, selectedPens, domain, connection }, dispatch } = useLfContext();
  const { sectionOptions } = useSectionOptions(undefined, systemId)
  const [loading, setLoading] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const mobile = useMediaQuery(`(max-width: 700px)`) || focus_view === true;

  // #region | Data fetch
  const { call: getLfSectionsInfo, data: lfSectionsState, doEmptyFeedLocation } = useSection(connection);
  const sections = useMemo( () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId] );

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // #endregion

  // Set initial section, pens and selected pens
  useEffect(() => {
    if (!sections || sections.length === 0) return;
  
    const first = sections[0];
  
    if (!first?.section_locations) return;
  
    dispatch({
      selectedSection: first,
      selectedPens: first.section_locations,
    });
  }, [dispatch, sections]);

  // #region | Handle Empty Selected Button
  const [timeChange, setTimeChange] = useState(false);
  const [timeChangeValue, setTimeChangeValue] = useState<number>(10);

  function handleTimeout(elementId: number) {
    setTimeout(() => {
      dispatch({ error: { Elementid: elementId, errorText: "" } });
    }, 2000);
  }

  // Empty Feed
  const handleEmptyFeed = async () => {
    abortPreviousOperation();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);

    const results = await doEmptyFeedLocation(
      selectedPens?.map(pen => pen.location_id) || [],
      DEF_TRIGGER_AMOUNT,
      DEF_PAUSE_TIME,
      2 * timeChangeValue /*TODO: Why is 2 getting added to this value??? magic number?*/,
      undefined,
      abortControllerRef.current.signal
    );

    if (!abortControllerRef.current) return;

    const newLocationResults: { [key: number]: boolean } = {};

    let errorOccured: boolean = false;

    for (let i = 0; i < results.length; i++) {
      let result = results[i];
      newLocationResults[result.id] = result.success;
      if (!result.success) {
        if (!errorOccured) {
          errorOccured = true;
          dispatch({ error: { Elementid: 1, errorText: t("systemControls.general.emptyingFailed", { defaultValue: "Emptying failed" }) } });
          handleTimeout(1);
        }
      }
    }

    if (!errorOccured) {
      dispatch({ error: { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) } });
      handleTimeout(2);
    }

    setLoading(false);
    dispatch({locationResults: newLocationResults})
    abortControllerRef.current = null;
  }

  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  function resetResultState() {
    abortPreviousOperation();
    setLoading(false);
    dispatch({locationResults: []})
  }

  // Button Handler
  const handleOnClick = () => {
    resetResultState();
    handleEmptyFeed();
  };
  // #endregion

  if (!sections) return null;
  const currentIndex = sections.findIndex(
    (s) => s.section_id === selectedSection?.section_id
  );

  return (
    selectedSection && (
      <NewModal
        header={t("systemControls.general.systemControls", {
          defaultValue: "System Controls",
        })}
        modalState={isModalOpen}
        responsiveOverrides={{
          focus_view: focus_view,
          mobile: mobile
        }}
        predefinedButtons={{
          onClose: () => { dispatch({selectedSection: sections[0]}); resetResultState(); onClose() },
          onNext: () => { dispatch({selectedSection: sections[(currentIndex + 1) % sections.length]}); resetResultState() },
          onPrev: () => { dispatch({ selectedSection: sections[(currentIndex - 1 + sections.length) % sections.length] }); resetResultState() },
        }}
        subHeader={selectedSection?.name}
        overrides={{
          backgroundColor: colors.greyLightTertiary,
          mainContentPadding: MainContentPaddingTypes.primary,
          mainContentScroll: mobile ? true : false,
          headerContentHeight: focus_view ? "60px" : undefined
        }}
        headerContent={
          <NewSelect
            options={sectionOptions ?? []}
            key={selectedSection.section_id}
            onChange={(i) => { resetResultState(); dispatch({selectedSection: sections.find(section => section.section_id === i?.id)})}}
            valueOverrides={{
              defaultValue: sectionOptions?.find(option => option.id === selectedSection.section_id)
            }}
            fieldOverrides={{
              fieldWidth: mobile ? "120px" : "160px",
              focus_view: focus_view,
              menuWidth: focus_view ? "70vw" : undefined,
              menuFloat: MenuFloats.center 
            }}
          />
        }
        mainContent={
          <SystemControlsInner
            focus_view={focus_view}
            selectContent={
              <div style={{ height: focus_view ? "40px" : "30px", display: "flex", alignItems: "center", gap: "20px"}}>
                <CheckBox
                  focus_view={focus_view}
                  checkStyle={CheckStyles.grey}
                  onChange={() => {setTimeChange(!timeChange); if (timeChangeValue !== 10) setTimeChangeValue(10)}}
                  isChecked={timeChange}
                  text={t("systemControls.general.changeTime", { defaultValue: "Change Time" })}
                />
                  {timeChange ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "5px"}}>
                    <NewInput
                      placeholder={t("units.min", { defaultValue: "min" })}
                      onChange={(e) => setTimeChangeValue(Number(e))}
                      fieldOverrides={{
                        fieldStyle: NewFieldStyles.greyTertiary,
                        fieldWidth: "100px",
                        focus_view: focus_view
                      }}
                      valueOverrides={{
                        valueType: "number",
                        defaultValue: timeChangeValue
                      }}
                    />
                    <p style={{...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4), color: colors.greyDarkPrimary}}> {t("units.min", { defaultValue: "min" })} </p>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            }
            button={{
              content: t("systemControls.general.emptySelected", { defaultValue: "Empty Selected" }),
              startEnhancer: "Empty",
              onClick: handleOnClick,
              disabled: loading
            }}
          />
        }
      />
    )
  );
}